import animate from '@play-co/timestep-core/lib/animate';
import { playExtraMovesOfferAnimation } from '../movesPinch/extraMovesOffer.animation';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  ImageScaleView,
  MaskView,
  TintFilter,
} from '@play-co/timestep-core/ui';
import SparkleFx from '../team/components/SparkleFx';
import { animDuration, delay } from 'src/lib/utils';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { getContinueRewardIcon } from '../match2.icons';
import { matchTheme } from 'src/game/theme/match2/Match2Theme';
import { GemsWallet } from 'src/game/components/GemsWallet';
import { movesPinchCheck } from 'src/lib/pinch';
import {
  getMatch2CloseButtonX,
  match2CloseButtonY,
} from './match2PopupCloseButton';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2MovePinch extends PopupBasic {
  private close: CloseHandler;
  private contentWrapper: View;
  private prompt: LangBitmapFontTextView;
  private ctaButton: ButtonScaleViewWithText;
  private gemsWallet: GemsWallet;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private vfx!: gsap.core.Tween;
  private isPopupHidden = false;
  /** Existing moves icon created by init before the opening animation. */
  private movesBadge!: ImageScaleView;
  /** Move count updated during the increased-offer animation. */
  private movesLabel!: LangBitmapFontTextView;
  /** Regular amount passed to init, retained for the opening animation. */
  private regularMoves = 0;
  /** Bonus passed to init, revealed after the popup opens. */
  private extraMoves = 0;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = 676;
    const height = 712;

    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      darkerBg: true,
      offsetY: -50,
      width,
      height,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.title.updateOpts({ color: '#E25959' });
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.95)',
    });
    this.bg.onClick = () => this.toggleShowPuzzle();

    this.gemsWallet = new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    new ButtonScaleView({
      ...uiConfig.buttons.close,
      superview: this.box,
      x: getMatch2CloseButtonX(this.box.style.width),
      y: match2CloseButtonY,
      onClick: async () => {
        sounds.playSound('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_BACK,
      },
    });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    this.prompt = new LangBitmapFontTextView({
      superview: this.contentWrapper,
      size: 35,
      font: 'Body',
      color: '#985C25',
      localeText: () => i18n('match2.movesPinch.message4'),
      width,
      height: 80,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height - 210,
      align: 'center',
      verticalAlign: 'center',
    });

    this.ctaButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.contentWrapper,
      width: 477,
      height: 110,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height - 100,
      fontSize: 50,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 54,
        height: 45,
        offset: { x: 190, y: 0 },
      },
      labelOffsetX: -35,
      localeText: () => i18n('match2.movesPinch.cta'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.PURCHASE_PINCH_OK,
      },
    });

    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 150 + this.box.style.y + this.box.style.height * 0.5,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      // Preload the supplied sparkle before the offer animation starts.
      'assets/ui/match2/movepinch/sparkle.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/match2/movepinch/bg_move_pinch.png',
      'assets/ui/match2/common/fx_shine.png',
      'assets/ui/match2/common/pinch_moves.png',
      'assets/ui/match2/movepinch/bonus_underlay_move_pinch.png',
      matchTheme().getUIAsset(`continue_bonus_panel.png`),
    ]);
  }

  private createHero(amount: number, continues: number) {
    // background
    const width = 586;
    const height = 336;
    const bg = new ImageScaleView({
      superview: this.contentWrapper,
      image: `assets/ui/match2/movepinch/bg_move_pinch.png`,
      width,
      height,
      centerOnOrigin: true,
      x: this.box.style.width * 0.5,
      y: 275,
    });

    // VFXs
    const mask = new MaskView({
      superview: bg,
      mask: `assets/ui/match2/movepinch/bg_move_pinch.png`,
      width,
      height,
    });

    const shine = new ImageScaleView({
      superview: mask,
      image: 'assets/ui/match2/common/fx_shine.png',
      width: 458,
      height: 376,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height * 0.5,
      filter: new TintFilter('rgba(255, 255, 255, 1)'),
      opacity: 0.6,
      scale: 1.75,
    });

    const sparkle = new SparkleFx({
      superview: mask,
      width: mask.style.width,
      height: mask.style.height,
      x: width * 0.5,
      y: height * 0.5,
      scale: 2,
      amount: 10,
    });

    this.vfx = gsap.to(shine.style, {
      duration: 30,
      r: Math.PI * 2,
      ease: 'none',
      repeat: -1,
      onUpdate: () => {
        sparkle.tick(0.001);
      },
    });

    // icon
    const icon = (this.movesBadge = new ImageScaleView({
      superview: bg,
      image: `assets/ui/match2/common/pinch_moves.png`,
      width: 204,
      height: 204,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height * 0.5,
    }));

    // Keep the real label available for the bonus flight and count-up.
    this.movesLabel = new LangBitmapFontTextView({
      superview: icon,
      font: 'Title',
      color: '#FFFFFF',
      size: 80,
      x: icon.style.width - 50,
      y: icon.style.height - 100,
      centerOnOrigin: true,
      localeText: () => `+${amount}`,
      verticalAlign: 'center',
      align: 'center',
    });

    if (!continues) return;

    // corner background
    const size = 194;
    const corner = new ImageScaleView({
      superview: bg,
      image: `assets/ui/match2/movepinch/bonus_underlay_move_pinch.png`,
      width: size,
      height: size,
      x: bg.style.width - size,
      y: 5,
    });

    // bonus icon
    const panel = new ImageScaleView({
      superview: corner,
      image: matchTheme().getUIAsset(`continue_bonus_panel.png`),
      width: 145,
      height: 145,
      centerOnOrigin: true,
      x: 130,
      y: 40,
      r: 0.2,
    });

    getContinueRewardIcon()
      ?.get()
      .then((image) => {
        new ImageScaleView({
          superview: panel,
          image,
          width: panel.style.width,
          height: panel.style.height,
        });
      });

    if (continues > 3) {
      new LangBitmapFontTextView({
        superview: corner,
        font: 'Title',
        color: '#FFFFFF',
        size: 40,
        x: size,
        y: 36,
        centerOnOrigin: true,
        localeText: () => 'x2',
        verticalAlign: 'center',
        align: 'right',
      });
    }

    // bonus label
    new LangBitmapFontTextView({
      superview: corner,
      font: 'Title',
      color: '#FD791A',
      size: 50,
      x: size + 5,
      y: 65,
      centerOnOrigin: true,
      localeText: () => i18n('match2.movesPinch.bonus'),
      verticalAlign: 'center',
      align: 'right',
    });
  }

  public init({
    amount,
    extraMoves = 0,
    continues = 0,
    price,
  }: {
    amount: number;
    extraMoves?: number;
    continues?: number;
    price: number;
  }) {
    super.init({
      title: i18n(`match2.movesPinch.title3`),
    });

    // Show the regular amount until the opening animation reveals the bonus.
    this.regularMoves = amount;
    this.extraMoves = extraMoves;
    this.createHero(amount, continues);
    this.setNumMoves(amount);

    this.ctaButton.label.localeText = () =>
      `${i18n('match2.movesPinch.cta')}  ${price}`;
    this.ctaButton.onClick = async () => {
      sounds.sfx.play('ok');
      const success = await movesPinchCheck(price, 'match2_shop_moves');
      if (success) this.close(true);
    };
  }

  /**
   * Keep the move count and purchase description in sync.
   * @param moves Move amount to display.
   */
  private setNumMoves(moves: number): void {
    // Update both labels during the offer count-up.
    this.movesLabel.localeText = () => `+${moves}`;
    this.prompt.localeText = () =>
      i18n('match2.movesPinch.message4', { moves });
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    // Wait for the opening tween before taking over its input blocker.
    await super.fadeIn();
    await animate(this.box).toPromise();
    this.overlay.show();
    await this.tweenToggleMessageScaleY(1);

    await playExtraMovesOfferAnimation({
      badge: this.movesBadge,
      label: this.movesLabel,
      regularMoves: this.regularMoves,
      setNumMoves: (moves) => this.setNumMoves(moves),
      bonusMoves: this.extraMoves,
    });
    this.overlay.hide();
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
    await delay(animDuration * 2);
    this.vfx?.kill();
  }
}
